import { useState } from 'react';
import { Alert, Button, Card, Icon, Table, type Column } from '@evogard/ui';
import type { ManagementConsultants } from '@evogard/contract';
import { count, type ClaimFlowPeriod } from '@evogard/domain';
import { useManagementConsultants } from './useManagement.js';

/**
 * Os sinistros por consultor (29/10/2026, pedido do dono): quantos sinistros
 * de associado abertos no período são de carros de cada consultor — o
 * voluntário do ERP que fez o contrato do veículo.
 *
 * Barras deitadas, do consultor com mais sinistros para o com menos, só os
 * primeiros no gráfico; a tabela tem todos. A parte escura da barra é o que
 * ainda está em processo, a clara o que já saiu dele — e os dois números
 * estão escritos, porque a cor sozinha não diz quanto.
 */

/** Quantos consultores o gráfico desenha; a tabela tem todos. */
const SHOWN = 12;

type Consultant = ManagementConsultants['consultants'][number];

/** O nome do consultor, ou o código do ERP quando o cadastro da casa não o tem. */
function nameOf(c: Consultant): string {
  if (c.name) return c.name;
  if (c.erpCode) return `Voluntário ${c.erpCode} (fora do cadastro)`;
  return 'Sem consultor no ERP';
}

const share = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);

export function ClaimConsultantsChart({ days }: { days: ClaimFlowPeriod }) {
  const [asTable, setAsTable] = useState(false);
  const query = useManagementConsultants(days);
  const data = query.data;

  return (
    <Card
      as="figure"
      title="Sinistros por consultor"
      descricao="O consultor do carro de cada sinistro do associado, do que tem mais para o que tem menos."
      actions={
        !!data?.total &&
        !query.isError && (
          <Button variant="discreto" size="sm" onClick={() => setAsTable((v) => !v)}>
            <Icon nome={asTable ? 'grafico' : 'grade'} size={16} />
            {asTable ? 'Ver gráfico' : 'Ver tabela'}
          </Button>
        )
      }
    >
      {query.isError ? (
        <Alert tone="erro" title="Não conseguimos carregar os sinistros por consultor">
          Nada foi desenhado, para não mostrar número velho como se fosse atual.
          <span className="mt-1 block font-mono text-xs">{query.error.message}</span>
        </Alert>
      ) : !data ? (
        <div
          className="h-64 animate-pulse rounded-borda bg-superficie-suave"
          aria-busy="true"
        />
      ) : data.total === 0 ? (
        <p className="py-10 text-center text-sm text-texto-suave">
          Nenhum sinistro de associado aberto nos últimos {days} dias. Quando houver, o
          gráfico mostra de que consultor é o carro de cada um.
        </p>
      ) : asTable ? (
        <ConsultantsTable data={data} />
      ) : (
        <ConsultantsFigure data={data} />
      )}
      {data && data.total > 0 && (
        <figcaption className="mt-3 text-xs text-texto-suave">
          {count(data.total, 'sinistro', 'sinistros')} de{' '}
          {count(data.consultants.length, 'consultor', 'consultores')}
          {data.consultants.length > SHOWN &&
            !asTable &&
            ` — o gráfico mostra os ${SHOWN} primeiros, a tabela mostra todos`}
          {data.withoutVehicle > 0 &&
            `; ${count(data.withoutVehicle, 'aviso', 'avisos')} (${share(data.withoutVehicle, data.total)}%) sem o carro no cadastro da casa, e por isso sem consultor`}
          .
        </figcaption>
      )}
    </Card>
  );
}

function ConsultantsFigure({ data }: { data: ManagementConsultants }) {
  const shown = data.consultants.slice(0, SHOWN);
  const max = Math.max(1, ...shown.map((c) => c.total));
  const pct = (n: number) => `${(n / max) * 100}%`;
  return (
    <div className="flex flex-col gap-3">
      <ol className="flex flex-col gap-2" aria-label="Sinistros por consultor">
        {shown.map((c) => (
          <li
            key={c.volunteerId ?? `erp:${c.erpCode}`}
            className="grid grid-cols-[minmax(0,12rem)_minmax(0,1fr)_minmax(0,5rem)] items-center gap-3 text-xs"
          >
            <span className="truncate text-texto" title={nameOf(c)}>
              {nameOf(c)}
            </span>
            <span className="flex h-2.5 overflow-hidden rounded-pilula bg-superficie-suave">
              <span className="h-full bg-primaria" style={{ width: pct(c.open) }} />
              <span
                className="h-full bg-primaria opacity-35"
                style={{ width: pct(c.total - c.open) }}
              />
            </span>
            <span className="text-right tabular-nums text-texto-suave">
              <b className="font-semibold text-texto">{c.total}</b>
              {c.open > 0 && <span className="ml-1">· {c.open} abertos</span>}
            </span>
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-texto-suave">
        <span className="flex items-center gap-1.5">
          <i aria-hidden className="h-2.5 w-4 rounded-pilula bg-primaria" />
          em processo
        </span>
        <span className="flex items-center gap-1.5">
          <i aria-hidden className="h-2.5 w-4 rounded-pilula bg-primaria opacity-35" />
          já saiu do processo (concluído, negado ou desistência)
        </span>
      </div>
    </div>
  );
}

function ConsultantsTable({ data }: { data: ManagementConsultants }) {
  const columns: Column<Consultant>[] = [
    { title: 'Consultor', cell: nameOf, alignment: 'inicio' },
    {
      title: 'Código no ERP',
      cell: (c) => c.erpCode ?? '—',
      alignment: 'inicio',
    },
    { title: 'Sinistros', cell: (c) => c.total, alignment: 'fim' },
    { title: 'Em processo', cell: (c) => c.open, alignment: 'fim' },
    {
      title: '% do total',
      cell: (c) => `${share(c.total, data.total)}%`,
      alignment: 'fim',
    },
  ];
  return (
    <Table
      columns={columns}
      items={data.consultants}
      rowKey={(c) => (c.volunteerId !== null ? `id:${c.volunteerId}` : `erp:${c.erpCode}`)}
      label={`Sinistros por consultor dos abertos nos últimos ${data.days} dias`}
      empty="Nenhum sinistro com consultor no período."
    />
  );
}
